import {Marked, marked} from 'marked'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {INLINE_ALLOWED_TAGS, sanitize, type SafeHTMLString, type SanitizedHTML} from '@github-ui/sanitize'

const LEGACY_DOMPURIFY_CONFIG = {
  FORBID_TAGS: ['style'],
  FORBID_ATTR: ['class', 'id', 'style'],
  ALLOW_DATA_ATTR: false,
  ALLOWED_TAGS: [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'h7',
    'h8',
    'br',
    'b',
    'i',
    'strong',
    'em',
    'a',
    'pre',
    'code',
    'img',
    'tt',
    'div',
    'span',
    'ins',
    'del',
    'sup',
    'sub',
    'p',
    'ol',
    'ul',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'blockquote',
    'dl',
    'dt',
    'dd',
    'kbd',
    'q',
    'samp',
    'var',
    'hr',
    'ruby',
    'rt',
    'rp',
    'li',
    'tr',
    'td',
    'th',
    's',
    'strike',
    'summary',
    'details',
    'picture',
    'source',
  ],
}

export interface Md2htmlTrustedTypesOptions {
  trustedTypes: true
}

function sanitizeMarkdown(dirtyHTML: string): SafeHTMLString
function sanitizeMarkdown(dirtyHTML: string, options: Md2htmlTrustedTypesOptions): SanitizedHTML
function sanitizeMarkdown(dirtyHTML: string, options?: Md2htmlTrustedTypesOptions): SafeHTMLString | SanitizedHTML {
  if (options?.trustedTypes && isFeatureEnabled('sanitize_preset_md2html')) {
    return sanitize(dirtyHTML, {preset: 'markdown'})
  }

  /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
     Legacy fallback while the named preset rolls out behind sanitize_preset_md2html. */
  return sanitize(dirtyHTML, {...LEGACY_DOMPURIFY_CONFIG, RETURN_TRUSTED_TYPE: false})
}

// This function checks if the markdown can be parsed optimistically
// Task lists can not be parsed optimistically since they require styling to avoid jitter
// and this is not allowed for security reasons
export function canBeOptimisticallyParsed(markdown: string): boolean {
  // Check for the presence of certain syntax that indicates the markdown can be parsed optimistically
  // Matches lines that start with - [ ] or - [x] (optionally *, +, or -), possibly with whitespace before
  const optimisticSyntax = /^\s*[-*+] \[[ xX]\] .*/m
  return !optimisticSyntax.test(markdown)
}

export function md2html(markdown: string): SafeHTMLString
export function md2html(markdown: string, options: Md2htmlTrustedTypesOptions): SanitizedHTML
export function md2html(markdown: string, options?: Md2htmlTrustedTypesOptions): SafeHTMLString | SanitizedHTML {
  try {
    const dirtyHTML = marked.parse(markdown, {
      gfm: true,
      breaks: true,
      silent: true, // Prevents errors from being thrown for invalid markdown
    })
    return options ? sanitizeMarkdown(dirtyHTML, options) : sanitizeMarkdown(dirtyHTML)
  } catch {
    // If there's an error in parsing, return an empty string
    return options ? sanitizeMarkdown('', options) : sanitizeMarkdown('')
  }
}

const disabled = () => undefined

const titleMarked = new Marked({
  gfm: false,
  breaks: false,
  tokenizer: {
    escape: disabled,
    tag: disabled,
    link: disabled,
    reflink: disabled,
    emStrong: disabled,
    br: disabled,
    del: disabled,
    autolink: disabled,
    url: disabled,
  },
})

// Ideally we'd use mintUnsafeStaticHTML(), but the trusted types type is not assignable to `string` which creates
// wide-reaching type errors across the app
// eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
const emptyHtml = '' as SafeHTMLString

/**
 * Render Markdown inline title. Only backticks (inline code) are supported. Parallels
 * `lib/github/goomba/title_markdown_filter.rb`, except it doesn't currently support custom emoji shortcodes.
 */
export function md2htmlTitle(markdown: string) {
  try {
    // `parseInline` runs synchronously here, but its return type is `string | Promise | undefined`
    // — guard so `sanitize` only sees a string.
    const dirtyHTML = titleMarked.parseInline(markdown, {gfm: true, breaks: true, silent: true})
    if (typeof dirtyHTML !== 'string') return emptyHtml
    /* eslint-disable-next-line @github-ui/github-monorepo/no-custom-sanitize-config --
       Existing custom sanitizer config; migrate to a named preset or VerifiedHTML when changed.
       We should probably introduce a new config for titles which only allows `code` tags. */
    return sanitize(dirtyHTML, {
      ...LEGACY_DOMPURIFY_CONFIG,
      ALLOWED_TAGS: [...INLINE_ALLOWED_TAGS],
      ALLOWED_ATTR: [],
      ALLOW_ARIA_ATTR: false,
      RETURN_TRUSTED_TYPE: false,
    })
  } catch {
    return emptyHtml
  }
}
